/** Device linking, POS.md §12.9.2 "App linking" (O2). */

export const DEVICE_ENDPOINTS = {
  /** Staff: linked devices (query-assist list). */
  list: '/api/backoffice/device',
  /** Staff: a code to connect a web till or the POS app (Settings → POS → Connect). */
  linkCode: '/api/backoffice/device/link-code',
  /** Staff: unlink (`:id`). */
  unlink: '/api/backoffice/device/:id',
  /** Mobile, tenantless: where a code's business lives. */
  redeem: '/api/backoffice/device/redeem',
  /** On the tenant (pos-web, and mobile after redeem): use the code and link. */
  link: '/api/backoffice/device/link',
} as const

/** Sent by the device on every POS sync call; the server refuses unknown or unlinked ids. */
export const DEVICE_ID_HEADER = 'X-Device-Id'

/** `code` on the server's 403 when the device check fails. */
export const DEVICE_ERROR_CODES: readonly string[] = ['DEVICE_NOT_LINKED', 'DEVICE_UNLINKED']
export type DeviceErrorCode = 'DEVICE_NOT_LINKED' | 'DEVICE_UNLINKED'

export type DevicePlatform = 'android' | 'ios' | 'web'

/** A linked POS app or till. */
export interface LinkedDevice {
  _id: string
  device_uid: string
  /** The branch it works in, from its Connect code (POS.md §18.2); null for devices linked before that. */
  branch_id: string | null
  branch?: { _id: string; name: string } | null
  platform: DevicePlatform
  name: string | null
  device_model: string | null
  os: string | null
  linked_at: string
  last_seen_at: string | null
}

/** What a device says about itself when it links. */
export interface DeviceInfo {
  device_uid: string
  platform?: DevicePlatform
  name?: string
  device_model?: string
  os?: string
}

/** `code` from Settings → POS → Connect, scanned from its QR or typed. */
export interface DeviceLinkRequest extends DeviceInfo {
  code: string
  platform: DevicePlatform
}

/** 6 characters, valid 10 minutes, single use. Settings → POS → Connect shows it as a QR (the code alone) and text. */
export interface DeviceLinkCode {
  code: string
  expires_at: string
  branch: { _id: string; name: string }
}

/** Settings → POS → Connect: the branch the device will work in (POS.md §18.2). */
export interface DeviceLinkCodeRequest {
  branch_id: string
}

/** Mobile redeem result: how the app reaches its business from then on. */
export interface DeviceRedeemResult {
  name: string
  /** Sent as `X-Client-Host` on every call. */
  client_host: string
  api_base_url: string
  /** The branch the device will work in; null for codes made before branches were bound. */
  branch_id: string | null
}
